/* eslint-disable react/jsx-curly-newline */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { orderBy } from 'lodash';
import {
  Button,
  AFPTable,
  PageTitle,
  EmptyState,
} from '@gsa/afp-component-library';
import { useNavigate } from 'react-router-dom';
import GenericMessagesToast from '@/components/Toast/generic-messages-toast';
import { emDashUnicode } from '@/utilities/constants';
import { useReviewSubmit } from './review-submit-provider';
import OpenPeriodTable from './open-period-table';
import { ReopenPeriodcolumns } from './helpers';
import SolicitationStepper, {
  isSolicitationSubmittable,
} from '../../components/solicitation-stepper';
import {
  solicitationNoOpenPeriodErrorNotifications,
  solicitationNoSinErrorNotifications,
} from '../../atoms/solicitaion-submit-error-atom';
import { PURCHASE_TYPE_MAP } from '../../constants';
import ViewSolicitationStandardItems from '../../view-solicitation/view-solicitation-std-items';
import {
  processOpenPeriod,
  processReopenPeriods,
} from '../../utils/open-reopen-period-util';
import SolicitationStatusBadge from '../../components/solicitation-status-badge';
import SolicitationPeriodsTableSubComponent from '../../components/solicitation-periods-table-sub-comp';
import InactiveSIAlert from '../../components/solicitation-alert/inactive-si-alert';
import './review.scss';
import SolicitationNotification from '../../utils/solicitation-notification';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';

const ReviewSubmitSolicitation = () => {
  const {
    solicitation,
    submitSolicitation,
    SIcount,
    getFvsCodes,
    standardItems,
  } = useReviewSubmit();

  const isSubmittable = isSolicitationSubmittable(solicitation);

  const [noOpenPeriodErrnotifications] = useRecoilState(
    solicitationNoOpenPeriodErrorNotifications,
  );
  const [noSinErrnotifications] = useRecoilState(
    solicitationNoSinErrorNotifications,
  );

  const [openPeriod, setOpenPeriod] = useState({});
  const [reopenPeriods, setReopenPeriods] = useState([]);
  const [order, setOrder] = useState(['period', 'ASC']);
  const [, setSolicitationNotification] = useRecoilState(
    solicitationNotifications,
  );

  useEffect(() => {
    if (solicitation.solicitationPeriods.length > 0) {
      const ropenPeriods = processReopenPeriods(
        solicitation.solicitationPeriods,
      );
      let reopenIndex = 0;
      let midcycleIndex = 0;
      const rp = ropenPeriods.map((prd) => {
        if (prd.periodType === 'R') {
          ++reopenIndex;
          return {
            ...prd,
            period: `Reopen period ${reopenIndex}`,
          };
        }
        ++midcycleIndex;
        return {
          ...prd,
          period: `Midcycle period ${midcycleIndex}`,
        };
      });
      setReopenPeriods(rp);
      setOpenPeriod(processOpenPeriod(solicitation.solicitationPeriods));
    }
    if (solicitation.contractYear) {
      getFvsCodes({
        variables: {
          year: solicitation.contractYear,
          includeOnlyCommentable: false,
        },
      });
    }
  }, [solicitation, getFvsCodes]);

  const onSort = (newOrder) => {
    // requires asc/desc to be lowercase for lodash orderBy function
    setReopenPeriods(
      orderBy(
        [...(reopenPeriods ?? [])],
        newOrder[0]?.[0] ?? '',
        newOrder[0]?.[1]?.toLowerCase() ?? 'asc',
      ),
    );
    setOrder(newOrder);
  };

  const navigate = useNavigate();
  const navigateHome = () => {
    navigate('/catalog/solicitations'); // navigate back to listing page
  };
  const onClickPrev = () => {
    setSolicitationNotification([]);
    navigate(`/catalog/solicitations/edit/${solicitation.id}/assign-quantity`);
  };

  const onSubmit = () => {
    if (isSubmittable) {
      submitSolicitation({
        variables: { solicitationId: Number(solicitation.id) },
      });
    } else {
      navigateHome();
    }
  };

  return (
    <div className="margin-bottom-6" data-testId="submit-solicitation-wrapper">
      <SolicitationNotification isModal={false} />
      <InactiveSIAlert
        solicitationPrograms={solicitation.programs}
        contractYear={solicitation.contractYear}
      />
      <div className=" grid-row review-header">
        <PageTitle
          title={`${solicitation.solicitationType} Solicitation - ${solicitation.solicitationNumber}`}
          aria-label={`${solicitation.solicitationType} Solicitation - ${solicitation.solicitationNumber}`}
          className="margin-top-0"
          tabIndex="0"
        />
        <div className="statusBadgeContainer margin-left-1 margin-top-2">
          <SolicitationStatusBadge status={solicitation.status} />
        </div>
      </div>

      <SolicitationStepper
        currentStep={solicitation.bidType === 'IN_FLEET' ? 6 : 4}
        solicitation={solicitation}
      />
      <p className="grid-col-7" role="paragraph">
        Review your solicitation information for accuracy. To make changes to a
        section, use the Edit link for that section. You may also use the
        Previous button at the bottom of the page to navigate back to any step
        to make changes. Click Submit to set your solicitation to New status.
      </p>
      <br />
      <div className="solicitation-upiid-container">
        <div className="grid-row flex-justify">
          <h3 className="margin-bottom-1">Solicitation uPIID</h3>
          <Button
            variant="unstyled"
            label="Edit"
            leftIcon={{ name: 'edit' }}
            onClick={() =>
              navigate(`/catalog/solicitations/edit/${solicitation.id}`)
            }
          />
        </div>
        <hr className=" border-gray-1 margin-top-0" />
        <h4 className="margin-bottom-1">Solicitation uPIID number</h4>
        <span>{solicitation.solicitationNumber}</span>
        <div className="grid-row grid-col-5 grid-gap-2">
          <div className="year-container">
            <h4 className="margin-bottom-1">Contract year</h4>
            <span>{solicitation.contractYear}</span>
          </div>
          <div className="type-container">
            <h4 className="margin-bottom-1">Purchase type</h4>
            <span>{PURCHASE_TYPE_MAP[solicitation.purchaseTypeCode]}</span>
          </div>
          <div className="type-container">
            <h4 className="margin-bottom-1">Solicitation process</h4>
            <span>{solicitation.solicitationType}</span>
          </div>
        </div>
        <br />
      </div>
      <div className="solicitation-details-container padding-top-4">
        <div className="grid-row flex-justify">
          <h3 className="margin-bottom-1">Solicitation details</h3>
          <Button
            variant="unstyled"
            label="Edit"
            leftIcon={{ name: 'edit' }}
            onClick={() =>
              navigate(`/catalog/solicitations/edit/${solicitation.id}`)
            }
          />
        </div>
        <hr className=" border-gray-1 margin-top-0" />
        <h4 className="margin-bottom-1">Solicitation title</h4>
        <span>{solicitation.title}</span>
        <h4 className="margin-bottom-1">Description</h4>
        <div className="grid-col-7">
          <span>{solicitation.description}</span>
        </div>
        <h4 className="margin-bottom-1">Solicitation posting</h4>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label={
              solicitation.samUrl ? solicitation.samUrl : emDashUnicode
            }
          >
            <span>
              {solicitation.samUrl ? (
                <a href={solicitation.samUrl}>{solicitation.samUrl}</a>
              ) : (
                emDashUnicode
              )}
            </span>
          </div>
        </div>
      </div>

      {solicitation.bidType === 'IN_FLEET' && (
        <>
          <div className="open-period-container padding-top-4">
            <div className="grid-row flex-justify">
              <h3 className="margin-bottom-1">Open period</h3>
              <Button
                variant="unstyled"
                label="Edit"
                leftIcon={{ name: 'edit' }}
                onClick={() => {
                  navigate(
                    `/catalog/solicitations/edit/${solicitation.id}/open-period`,
                  );
                }}
              />
            </div>
            <hr className=" border-gray-1 margin-top-0" />
            <div className="grid-row ">
              <div className="grid-col-fill">
                <GenericMessagesToast
                  allMessages={noOpenPeriodErrnotifications}
                  isModal={false}
                  onClose={() => {
                    return null;
                  }}
                />
              </div>
            </div>
            <OpenPeriodTable openPeriod={openPeriod} />
          </div>
          <div className="reopen-periods-container padding-top-4">
            <div className="grid-row flex-justify">
              <h3 className="margin-bottom-1">Additional periods</h3>
              <Button
                variant="unstyled"
                label="Edit"
                leftIcon={{ name: 'edit' }}
                onClick={() =>
                  navigate(
                    `/catalog/solicitations/edit/${solicitation.id}/reopen-period`,
                  )
                }
              />
            </div>
            <hr className=" border-gray-1 margin-top-0" />
            <AFPTable
              columns={ReopenPeriodcolumns}
              data={reopenPeriods}
              onSort={onSort}
              defaultSort={order}
              renderRowSubComponent={SolicitationPeriodsTableSubComponent}
            />
            {reopenPeriods.length === 0 && (
              <EmptyState
                hasBackground
                containerStyles="padding-y-5 margin-top-1 adding-y-8 margin-top-neg-2"
                data-testId="empty-state"
              />
            )}
          </div>
        </>
      )}

      <div className="standard-items-container padding-top-4">
        <div className="grid-row flex-justify">
          <h3 className="margin-bottom-1">Standard Items</h3>
          <Button
            variant="unstyled"
            label="Edit"
            leftIcon={{ name: 'edit' }}
            onClick={() =>
              navigate(
                `/catalog/solicitations/edit/${solicitation.id}/select-sin`,
              )
            }
          />
        </div>
        <hr className=" border-gray-1 margin-top-0" />
        <div className="grid-row">
          <div className="grid-col-fill">
            <GenericMessagesToast
              allMessages={noSinErrnotifications}
              isModal={false}
              onClose={() => {
                return null;
              }}
            />
          </div>
        </div>
        <div className="grid-row">
          <div
            className="grid-col-8"
            data-testid="view-solicitation-detail-standard-item-count"
            tabIndex="0"
            aria-label={`${SIcount} Standard Items selected`}
          >
            <span>
              <strong>{SIcount}</strong> Standard Items selected
            </span>
            <br />
          </div>
        </div>
        <ViewSolicitationStandardItems standardItems={standardItems} />
      </div>
      <div className="grid-row margin-top-6">
        <Button
          variant="outline"
          label="Previous"
          leftIcon={{ name: 'arrow_back' }}
          onClick={onClickPrev}
        />
        <Button
          variant="primary"
          label={isSubmittable ? 'Submit' : 'Close'}
          className="margin-left-1"
          onClick={onSubmit}
        />
      </div>
      {isSubmittable && (
        <div className="margin-top-3">
          <Button
            variant="unstyled"
            label="Keep as draft & return to Solicitation Listing page"
            onClick={navigateHome}
            className="text-underline"
            leftIcon={{
              name: 'arrow_back',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ReviewSubmitSolicitation;
